import {
  Component, Input, Output, Directive, forwardRef, ExistingProvider, EventEmitter, Renderer, ElementRef, PipeTransform, Pipe,
  Inject, LOCALE_ID
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { DecimalPipe, PercentPipe } from '@angular/common';
import { IntPercentPipe } from '../pipes/int-percent.pipe';

const CUSTOM_VALUE_ACCESSOR: ExistingProvider = { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => NumberFormatDirective), multi: true };
const _NUMBER_FORMAT_REGEXP = /^(\d+)?\.((\d+)(\-(\d+))?)?$/;
const _NUMBER_MATCH_REGEXP = /-?\d+(\.\d+)?/;


function precision(a: number) {
  if (!isFinite(a)) return 0;
  var e = 1, p = 0;
  while (Math.round(a * e) / e !== a) { e *= 10; p++; }
  return p;
}
function round_number(num: number, decimals: number) {
  return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
}

@Directive({
  selector: 'input[numberFormat]',
  host: { '(blur)': 'blurred($event.target.value)' },
  providers: [CUSTOM_VALUE_ACCESSOR]
})
export class NumberFormatDirective implements ControlValueAccessor {
  onChange = (_: any) => { };
  onTouched = () => { };
  blurred(value: any) {
    this.onChange(value);
    this.onTouched();
  }
  @Input('numberFormat') format: string;
  @Input('type') inputType: string;
  private _parsedFormat: {
    pipe: {
      instance: PipeTransform,
      map?: (x: number) => number
    },
    numbers: {
      minimumIntegerDigits: number,
      minimumFractionDigits: number,
      maximumFractionDigits: number
    },
    raw: string;
  };
  private parseFormat() {
    if (!this.format) throw `format string not set`;
    let split = this.format.split(':');
    const getPipe = (input: string): {
      instance: PipeTransform,
      map?: (x: number) => number
    } => {
      if (input === 'number') return { instance: new DecimalPipe(this.locale) };
      if (input === 'percent') return { instance: new PercentPipe(this.locale), map: x => parseFloat((x / 100).toFixed(precision(x) + 2)) };
      if (input === 'intPercent') return { instance: new IntPercentPipe(this.locale) };
      throw `unknown formatter pipe: ${input}`;
    };

    let digits = split[1];
    var minInt = 1, minFraction = 0, maxFraction = 3;
    if (!!digits) {
      var parts = _NUMBER_FORMAT_REGEXP.exec(digits);
      if (!(parts)) {
        throw `${digits} is not a valid digit info for number pipes`;
      }
      if (!!parts[1]) {  // min integer digits
        minInt = parseInt(parts[1]);
      }
      if (!!parts[3]) {  // min fraction digits
        minFraction = parseInt(parts[3]);
      }
      if (!!parts[5]) {  // max fraction digits
        maxFraction = parseInt(parts[5]);
      }
    }
    this._parsedFormat = {
      pipe: getPipe(split[0]), numbers: {
        minimumIntegerDigits: minInt,
        minimumFractionDigits: minFraction,
        maximumFractionDigits: maxFraction,
        //currency: currency,
        //currencyAsSymbol: currencyAsSymbol TODO Support these
      }, raw: digits
    };

  }

  constructor(private _renderer: Renderer, private _elementRef: ElementRef, @Inject(LOCALE_ID) private locale: string) { }
  formatToString(value: number): string {
    if (!this._parsedFormat) this.parseFormat();
    var pipe = this._parsedFormat.pipe;
    return pipe.instance.transform(value, this._parsedFormat.raw);
  }
  parseToNumber(value: string): number {
    value = value.replace(/,| /g, ""); //strip out commas and whitespaces
    let match = _NUMBER_MATCH_REGEXP.exec(value);
    if (!match) {
      this._renderer.setElementProperty(this._elementRef.nativeElement, 'value', "");
      return undefined;
    }
    let number = parseFloat(match[0])
    if (isNaN(number)) {
      this.writeValue(null);
      return null;
    }
    if (this._parsedFormat.numbers.maximumFractionDigits !== undefined) {
      number = round_number(number, this._parsedFormat.numbers.maximumFractionDigits);
    }
    var pipe = this._parsedFormat.pipe;
    if (pipe.map) number = pipe.map(number);
    this.writeValue(number);
    return number;
  }
  writeValue(value: number): void {
    if (this.inputType === 'number') {
      this._renderer.setElementProperty(this._elementRef.nativeElement, 'value', value);
    }
    else {
      let text = this.formatToString(value);
      this._renderer.setElementProperty(this._elementRef.nativeElement, 'value', text);
    }
  }

  registerOnChange(fn: (_: number) => void): void {
    this.onChange = (value) => { fn(value == '' ? null : this.parseToNumber(value)); };
  }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
}


// WEBPACK FOOTER //
// F:/Agent/vsts-agent1/_work/76/s/CIA.ClientSide/~/@wtw/toolkit/src/directives/number-format.directive.ts